// Iowa State University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Iowa State's Common Data Set 2025-26 (CDS-25-26.pdf), linked from the
//    Office of Institutional Research's Common Data Set page: C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table; C8 test
//    policy for Fall 2027; C14/C16 (no closing date, rolling decisions);
//    C21/C22 (no early plans). Iowa State ticked the G0 box saying its
//    2026-2027 costs were not yet final (due July 1), so its G1/G5 rows are
//    not used; the costs come from the financial aid office instead.
//  - Iowa State Admissions: First-year Students (Regent Admission Index,
//    test-optional policy, course requirements, extra courses for
//    Engineering and Liberal Arts and Sciences), Apply ("Iowa State does not
//    make admission decisions by major") and the First-Year Checklist (merit
//    scholarship priority date March 1, May 1 for priority housing). None of
//    them publishes an application deadline or decision dates.
//  - Office of Student Financial Aid, Cost of Attendance, Undergraduate
//    Students, 2026-2027 academic year ("estimates ... subject to change"):
//    the "Full rate reference" table, resident and non-resident, on campus,
//    dependent student. The page shows no totals; the totals here are the
//    sums of its items.
//  - College Scorecard, school id 153603 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Iowa State University Common Data Set 2025-2026',
        url: 'https://www.ir.iastate.edu/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Iowa State Admissions, First-year Students',
        url: 'https://www.iastate.edu/admission-and-aid/admissions/first-year-students',
        read: '2026-10-01',
    },
    apply: {
        label: 'Iowa State Admissions, Apply',
        url: 'https://www.iastate.edu/admission-and-aid/apply',
        read: '2026-10-01',
    },
    checklist: {
        label: 'Iowa State Admissions, First-Year Checklist',
        url: 'https://www.iastate.edu/admission-and-aid/admissions/first-year-students/first-year-checklist',
        read: '2026-10-01',
    },
    costs: {
        label: 'Iowa State Office of Student Financial Aid, Cost of Attendance: Undergraduate Students',
        url: 'https://financialaid.iastate.edu/getting-started/cost/cost-of-attendance/undergraduate-students/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?153603-Iowa-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'iowa-state-university',
    kbId: 'iowa_state_university',
    name: 'Iowa State University',
    shortName: 'Iowa State',
    location: 'Ames, IA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Iowa State brand color palette); decoration only.
    branding: {
        primary: '#C8102E',
        secondary: '#F1BE48',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/iowa_state_university_logo.png',
    },

    // The direct answer and short FAQ (#4229): every figure restates one the
    // page already shows, from the source named beside it.
    // The search it answers: iowa state university acceptance rate.
    faq: {
        answer: { text: 'Iowa State University admitted 87.9% of first-year applicants for fall 2025, 21,652 of the 24,625 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Iowa State acceptance rate?', a: 'For fall 2025 entry, Iowa State admitted 21,652 of 24,625 first-year applicants, an acceptance rate of 87.9%, and 6,160 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Iowa State acceptance rate for out-of-state students?', a: 'For fall 2025, Iowa State admitted 14,133 of 15,521 out-of-state first-year applicants, 91.1%. For Iowa residents it was 5,452 of 5,921, or 92.1%.', source: 'cds', where: 'section C1, residency table' },
            { q: 'Does Iowa State have an application deadline?', a: 'Iowa State reports no application closing date. It admits on a rolling basis and starts sending decisions 2 days after your application arrives.', source: 'cds', where: 'sections C14 and C16' },
            { q: 'Is Iowa State test-optional?', a: 'Yes. Iowa State is test-optional for first-year applicants, and you choose whether to self-report an ACT, SAT or CLT score. Without a score, admission is based on your high school GPA and the core course requirements.', source: 'firstYear' },
            { q: 'How does Iowa State use your GPA?', a: 'Your GPA feeds the Regent Admission Index (RAI): your ACT composite (or converted SAT or CLT) times 3, plus your cumulative GPA times 30, plus your years of high school core courses times 5. An RAI of 245 or more with the course requirements brings an automatic offer.', source: 'firstYear' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 24625,
        admitted: 21652,
        enrolled: 6160,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5921, admitted: 5452 },
            { group: 'Out-of-state', applied: 15521, admitted: 14133 },
            { group: 'International', applied: 3183, admitted: 2067 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Iowa State does not make admission decisions by major, so it publishes no acceptance rate by college or major; its Common Data Set gives one rate for the whole university.',
        source: 'apply',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Iowa State has no early decision or early action plan. It admits on a rolling basis with no closing date.',
        source: 'cds',
        where: 'sections C14, C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You choose a major when you apply, but Iowa State does not make admission decisions by major: admission rests on your Regent Admission Index (RAI). Two colleges ask for more high school courses:',
        rows: [
            { school: 'College of Engineering', requirement: 'Two years of a single world language, on top of the university\'s course requirements.' },
            { school: 'College of Liberal Arts and Sciences', requirement: 'A third year of social studies and two years of a single world language, on top of the university\'s course requirements.' },
        ],
        note: 'The RAI adds your ACT composite (or converted SAT or CLT) times 3, your cumulative GPA times 30, and your years of high school core courses times 5. An RAI of 245 or more with the course requirements brings an automatic offer; below 245, you may still be considered through an individual review.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Merit scholarship priority date', deadline: 'March 1', decision: null, binding: false },
            { plan: 'Rolling admission', deadline: 'No closing date', decision: null, binding: false },
        ],
        note: 'Iowa State asks you to apply for admission and merit scholarships in your senior year, with March 1 as its merit scholarship priority date, and to confirm by May 1 for priority housing. Its admissions pages publish no application deadline or decision dates; the Common Data Set reports no closing date and decisions sent on a rolling basis, starting 2 days after an application arrives.',
        source: 'checklist',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Iowa State is test-optional for first-year applicants. You choose whether to self-report an ACT, SAT or CLT score. Without a score, admission is based on your high school GPA and the core course requirements. With one, an SAT or CLT score is converted to its ACT equivalent for the RAI.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [9816, 29864] },
            { item: 'Required fees', amounts: [1586, 1586] },
            { item: 'Housing and food (on campus)', amounts: [11162, 11162] },
            { item: 'Books, course materials and supplies', amounts: [800, 800] },
            { item: 'Personal expenses', amounts: [2450, 2450] },
            { item: 'Transportation', amounts: [450, 900] },
        ],
        totals: [26264, 46762],
        note: 'For a full-time, dependent undergraduate living on campus, fall and spring. Iowa State calls these estimates, subject to change, and lists the items without a total; the totals are their sums. Sophomores, juniors and seniors in some colleges pay a higher tuition rate.',
        source: 'costs',
        netPrice: {
            average: 18517,
            byIncome: [
                { income: '$0 to $30,000', amount: 11957 },
                { income: '$30,001 to $48,000', amount: 12869 },
                { income: '$48,001 to $75,000', amount: 14013 },
                { income: '$75,001 to $110,000', amount: 19173 },
                { income: 'Over $110,000', amount: 22905 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Iowa State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
